import { CircleCheck, Clock, FlaskConical, HelpCircle, Hourglass, Loader2, OctagonAlert, type LucideIcon } from "lucide-react";
import type { FreshnessState } from "../../api/headerApi";
import type { LiveConnectionState } from "../../hooks/useLiveUpdates";

// Release 1.5.0 sync chip (O-R53): how each state looks. Every state has an icon AND words — never colour alone. The
// server decides the state and the words; this only maps them to the design system.

export interface ChipLook {
  icon: LucideIcon;
  className: string;
  spin?: boolean;
}

const LOOKS: Record<FreshnessState | "unknown", ChipLook> = {
  fresh: { icon: CircleCheck, className: "border-emerald-200 bg-emerald-50 text-emerald-800" },
  ageing: { icon: Clock, className: "border-amber-300 bg-amber-50 text-amber-900" },
  syncing: { icon: Loader2, className: "border-sky-200 bg-sky-50 text-sky-800", spin: true },
  failed: { icon: OctagonAlert, className: "border-red-200 bg-red-50 text-red-700" },
  waiting: { icon: Hourglass, className: "border-sky-200 bg-sky-50 text-sky-800" },
  practice: { icon: FlaskConical, className: "border-amber-200 bg-amber-50 text-amber-800" },
  unknown: { icon: HelpCircle, className: "border-slate-200 bg-white text-slate-600" }
};

export function chipLook(state: FreshnessState | null | undefined): ChipLook {
  return (state && LOOKS[state]) || LOOKS.unknown;
}

/** How the open page keeps itself current, in words (live-1-5's connection state); null when there is nothing to say. */
export function liveWords(live: Pick<LiveConnectionState, "status" | "transport">): string | null {
  switch (live.status) {
    case "live":
      return "Live — changes appear within seconds";
    case "reconnecting":
      return "Reconnecting… pages are checked every 30 s meanwhile";
    case "polling":
      return "Updates every 30 s";
    default:
      return null;
  }
}

/** How often the chip asks again: often while a sync runs; rarely when live pushes keep it current. */
export function freshnessPollMs(running: boolean, live: Pick<LiveConnectionState, "status">): number {
  if (running) return 10_000;
  return live.status === "live" ? 300_000 : 60_000;
}

/** The chip's accessible name: the full text even when the phone shows only the icon. */
export function chipAriaLabel(label: string | null | undefined, words: string | null | undefined): string {
  const l = label?.trim() || "Data age unknown";
  return words && words.trim() && words.trim() !== l ? `${l} (${words.trim()}). Show data sources` : `${l}. Show data sources`;
}
